import { useState, useEffect, useCallback, useRef } from "react";
import { FiPlus } from "react-icons/fi";
import api from "../../utils/api";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getInitials } from "../../utils/helpers";
import StatusViewer from "./StatusViewer";
import StatusCreator from "./StatusCreator";
import { StatusSkeleton } from "./SkeletonLoader";
import styles from "./StatusList.module.css";

const StatusList = () => {
  const { user }    = useAuthStore();
  const socketRef   = useSocket();
  const fetchTimer  = useRef(null);

  const [groups,       setGroups]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [viewerOpen,   setViewerOpen]   = useState(false);
  const [viewerIndex,  setViewerIndex]  = useState(0);
  const [creatorOpen,  setCreatorOpen]  = useState(false);

  const [error, setError] = useState(null);

  const fetchStatuses = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get("/status");
      const statuses = res.data.statuses || [];
      setGroups(statuses);
      try { sessionStorage.setItem("chatify-statuses", JSON.stringify(statuses)); } catch {}
    } catch (err) {
      setError(err.message);
      // Auto retry after 3s on network error
      if (!err.response) {
        setTimeout(() => fetchStatuses(), 3000);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Pre-populate from cache immediately so page doesn't show "Loading..."
  useEffect(() => {
    const cached = sessionStorage.getItem("chatify-statuses");
    if (cached) {
      try { setGroups(JSON.parse(cached)); setLoading(false); } catch {}
    }
  }, []);

  const [newStatusCount, setNewStatusCount] = useState(0);

  useEffect(() => {
    fetchStatuses();
    fetchTimer.current = setInterval(fetchStatuses, 30000);
    return () => clearInterval(fetchTimer.current);
  }, [fetchStatuses]);

  // Real-time socket updates
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;
    const onNew = ({ userId: newStatusUserId } = {}) => {
      fetchStatuses();
      // Only show badge for other people's statuses, not your own
      if (newStatusUserId && newStatusUserId !== user._id) {
        setNewStatusCount((c) => c + 1);
      }
    };
    const onDeleted = () => fetchStatuses();
    const onReaction = ({ statusOwnerId } = {}) => {
      // Do NOT increment the friends badge — reactions are on YOUR status
      // The green dot + toast already handles reaction notifications
    };
    socket.on("status:new",      onNew);
    socket.on("status:deleted",  onDeleted);
    socket.on("status:reaction", onReaction);
    return () => {
      socket.off("status:new",      onNew);
      socket.off("status:deleted",  onDeleted);
      socket.off("status:reaction", onReaction);
    };
  }, [socketRef?.current, fetchStatuses]);

  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpen(true);
    setNewStatusCount(0);
  };

  const handleDeleted = (deletedId) => {
    setGroups((prev) =>
      prev
        .map((g) => ({ ...g, statuses: g.statuses.filter((s) => s._id !== deletedId) }))
        .filter((g) => g.statuses.length > 0)
    );
  };

  const myGroup   = groups.find((g) => g.user._id === user._id);
  const myIndex   = myGroup ? groups.indexOf(myGroup) : -1;
  // My ring is faded if I have statuses but all are "old" (i.e. I've already seen them by being owner)
  const myRingFaded = myGroup && !myGroup.hasUnviewed;
  const friends   = groups.filter((g) => g.user._id !== user._id);

  if (loading) return <StatusSkeleton />;

  return (
    <div className={styles.container}>

      {/* ── My Status ── */}
      <div
        className={styles.myStatus}
        onClick={myGroup ? () => openViewer(myIndex) : () => setCreatorOpen(true)}
      >
        <div className={styles.myAvatarWrap}>
          <div className={`${styles.myRingWrap} ${myGroup ? (myRingFaded ? styles.myRingFaded : styles.myRingActive) : ""}`}>
            {user.avatar
              ? <img src={user.avatar} alt="" className={styles.myAvatar} />
              : <div className={styles.myAvatarFallback}>{getInitials(user.name)}</div>
            }
          </div>
          {/* Small + badge on avatar — hidden on mobile */}
          <button
            className={styles.addBtn}
            onClick={(e) => { e.stopPropagation(); setCreatorOpen(true); }}
          >
            <FiPlus size={12} />
          </button>
        </div>
        <div className={styles.myInfo}>
          <div className={styles.myName}>My Status</div>
          <div className={styles.myHint}>
            {myGroup
              ? `${myGroup.statuses.length} update${myGroup.statuses.length > 1 ? "s" : ""} · tap to view`
              : "Tap to add status update"}
          </div>
        </div>
        {/* Large + button on right — mobile only */}
        <button
          className={styles.addBtnLarge}
          onClick={(e) => { e.stopPropagation(); setCreatorOpen(true); }}
          title="Add status"
        >
          <FiPlus size={22} />
        </button>
      </div>

      {/* ── Friends' Status — ALWAYS visible ── */}
      <div className={styles.sectionLabel}>
        Friends' Status
        {newStatusCount > 0 && (
          <span className={styles.newBadge}>{newStatusCount}</span>
        )}
      </div>

      {friends.length > 0 ? (
        friends.map((group) => {
          const idx = groups.indexOf(group);
          return (
            <div key={group.user._id} className={styles.statusItem} onClick={() => openViewer(idx)}>


           {/* <div className={`${styles.ringWrap} ${group.hasUnviewed ? styles.unviewedRing : styles.viewedRing}`}>
                {group.user.avatar
                  ? <img src={group.user.avatar} alt="" className={styles.avatar} />
                  : <div className={styles.avatarFallback}>{getInitials(group.user.name)}</div>
                }
              </div>

              */}
             <div className={styles.ringWrap}>
  {group.user.avatar
    ? <img src={group.user.avatar} alt="" className={styles.avatar} />
    : <div className={styles.avatarFallback}>{getInitials(group.user.name)}</div>
  }
  <div
    className={`${styles.friendRing} ${
      group.hasUnviewed ? styles.unseen : styles.seen
    }`}
  ></div>
</div>


              <div className={styles.info}>
                <div className={styles.name}>{group.user.name}</div>
                <div className={styles.time}>
                  {(() => {
                    const d = new Date(group.statuses[0].createdAt);
                    const now = new Date();
                    const isToday = d.toDateString() === now.toDateString();
                    const yesterday = new Date(now);
                    yesterday.setDate(now.getDate() - 1);
                    const isYesterday = d.toDateString() === yesterday.toDateString();
                    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                    if (isToday) return `Today at ${time}`;
                    if (isYesterday) return `Yesterday at ${time}`;
                    return d.toLocaleDateString([], { month: "short", day: "numeric" }) + ` at ${time}`;
                  })()}
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>👁️</div>
          <p className={styles.emptyText}>No status updates yet</p>
          <p className={styles.emptyHint}>Status updates from your friends will appear here</p>
        </div>
      )}

      {/* Viewer */}
      {viewerOpen && (
        <StatusViewer
          statusGroups={groups}
          startIndex={viewerIndex}
          onClose={() => { setViewerOpen(false); fetchStatuses(); }}
          onDeleted={handleDeleted}
        />
      )}

      {/* Creator */}
      {creatorOpen && (
        <StatusCreator
          onClose={() => setCreatorOpen(false)}
          onCreated={() => { fetchStatuses(); setCreatorOpen(false); }}
        />
      )}
    </div>
  );
};

export default StatusList;
